import { LowerCasePipe } from '@angular/common';
import { Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ProductPlacementComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/product-placement/product-placement.component';
import { IKpiCard } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { FindingCardGridComponent } from '@cta-analytics-dialog/deliverable-analytics-content/finding-cards/finding-card-grid/finding-card-grid.component';
import { FindingCardMenuComponent } from '@cta-analytics-dialog/deliverable-analytics-content/finding-cards/finding-card-menu/finding-card-menu.component';
import { summarizeFindings } from '@cta-analytics-dialog/deliverable-analytics-content/finding-cards/finding-card.util';
import { FindingCardsState } from '@cta-analytics-dialog/deliverable-analytics-content/finding-cards/finding-cards.state';
import {
  IFindingSpan,
  IFindingSpans,
} from '@cta-analytics-dialog/deliverable-analytics-content/graphs/graph-finding-spans.interface';
import { LineGraphComponent } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.component';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { EDeliverableType } from '@enums/campaign.enum';
import { IProductPlacementAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { EmbeddedVideoPlayerComponent } from '@shared/components/embedded-video-player/embedded-video-player.component';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { GraphUtilitiesService } from '@utilities/graph-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { StreamDetailsDeliverableBaseDirective } from '../../stream-details-deliverable-base.directive';
import { IProductPlacementStreamAnalyticsTableData } from './product-placement-stream-analytics.interface';

@Component({
  imports: [
    TranslateModule,
    LineGraphComponent,
    EmbeddedVideoPlayerComponent,
    ProductPlacementComponent,
    LowerCasePipe,
    FindingCardGridComponent,
    FindingCardMenuComponent,
    NgxSkeletonLoaderModule,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-product-placement-stream-analytics',
  templateUrl: './product-placement-stream-analytics.component.html',
})
export class ProductPlacementStreamAnalyticsComponent extends StreamDetailsDeliverableBaseDirective<
  IProductPlacementStreamAnalyticsTableData,
  IProductPlacementAnalytics
> {
  public override readonly deliverableType = EDeliverableType.productPlacement;
  public override readonly kpiCardData: IKpiCard | null = null;
  public override readonly displayedColumns: string[] = [];
  public override readonly dataSource: MatTableDataSource<IProductPlacementStreamAnalyticsTableData> =
    new MatTableDataSource();
  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.productPlacements?.find((p) => p.deliverableId === deliverableId);
    }

    return null;
  });

  public readonly findingCardGrid = viewChild(FindingCardGridComponent);
  public readonly findingCards = new FindingCardsState<IProductPlacementStreamAnalyticsTableData>({
    isPendingDeletion: (card): boolean => this.hasRowPendingDeletions(card),
    loading: (): boolean => !this.findingsLoaded() || this.dataCount() === null,
    manualDeliverableStatusConfig: this.manualDeliverableStatusConfig,
    onJump: (cardId): void => this.onDataPointClick(cardId),
    removedIds: (): ReadonlySet<string> => this.manualFindingStatusStore?.allRemovedIds() ?? new Set<string>(),
    status: (): EAnalyticsStatus | null => this.currentDeliverable()?.status ?? null,
  });
  public readonly findingSpans = signal<IFindingSpans>({
    items: [],
    legend: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.graph.productVisibleLegend'),
    status: EAnalyticsStatus.ok,
  });
  public readonly findingsSummary = computed(() =>
    summarizeFindings(this.findingCards.activeSortedCards(), this.findingSpans().items, 1000),
  );
  // Same status color as the graph's spans, for the card number badges.
  public readonly findingColor = computed(() =>
    this.graphUtilitiesService.getColorByStatus(this.findingSpans().status),
  );

  private readonly graphUtilitiesService = inject(GraphUtilitiesService);

  constructor() {
    super();

    effect(() => {
      if (this.executionAnalyticsWithFindings()) {
        this.setStatus();
        this.setStreamData();
      }
    });

    effect(() => {
      this.manualFindingStatusStore?.serverRemovedIds();

      if (this.executionAnalyticsWithFindings() && this.timelineData()) {
        this.prepareChartAndTableData();
      }
    });
  }

  public override onDataPointClick(findingId: string): void {
    super.onDataPointClick(findingId);
    this.findingCards.highlightedId.set(findingId);
    this.findingCardGrid()?.scrollToCard(findingId);
  }

  protected override setCounts(): void {
    return;
  }

  protected override setKeywordList(): void {
    return;
  }

  protected override prepareChartAndTableData(): void {
    const tableData: IProductPlacementStreamAnalyticsTableData[] = [];
    const spanRanges: Omit<IFindingSpan, 'number'>[] = [];
    const currentProductPlacementAnalytics = this.currentDeliverable();
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const findings = (currentProductPlacementAnalytics?.findings || []).filter((f) => !removedIds.has(f.id));

    if (findings.length > 0) {
      findings.forEach((finding) => {
        const { id } = finding;
        const entryTimestamp = this.dateUtils.parseDateTimeFromApi(finding.timestamp!).getTime();

        spanRanges.push({ end: entryTimestamp + (finding.duration ?? 0) * 1000, id, start: entryTimestamp });

        tableData.push({
          analyticsImage: finding.analyticsImage,
          duration: finding.duration,
          findingParentId: finding.parentId,
          id,
          number: 0,
          realTime: finding.timestamp,
          sortTime: entryTimestamp / 1000,
          timestamp: finding.timestamp,
          videoTimeLabel: AnalyticsDetailsUtilitiesService.formatVideoTime(finding.videoTime),
          videoTimeSeconds: finding.videoTime ?? 0,
        });
      });
    }

    const cards = this.findingCards.setCards(tableData);
    const numberById = new Map(cards.map((card) => [card.id, card.number]));

    this.dataSource.data = cards;
    this.dataCount.set(cards.length);
    this.findingSpans.update((findingSpans) => ({
      ...findingSpans,
      items: spanRanges.map((range) => ({ ...range, number: numberById.get(range.id) ?? 0 })),
      status: currentProductPlacementAnalytics?.status ?? EAnalyticsStatus.ok,
    }));
  }
}
